import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonRounded from "@mui/icons-material/PersonRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import User from "../../../../api/User";
import Service from "../../../../api/Service";
import dayjs from "dayjs";
import ToggleButton from "../../../ToggleButton";
import Booking, { BookingState } from "../../../../api/Booking";
import TextField from "../../../TextField";
import { useCookies } from "react-cookie";
import TabHeader from "../../../TabHeader";
import { parseWithOffset } from "../../../../utils/dayjsUtils";
import { useNavigate } from "react-router-dom";
import { TAG_COLORS } from "../../../../utils/tagColors";
import BottomButton from "../../../BottomButton";
import FormattedTime from "../../../FormattedTime";
import FormattedPrice from "../../../FormattedPrice";
import ContentAreaWrapper from "../../../ContentAreaWrapper";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../../constants/styles";
import BookingForClientChip from "./BookingForClientChip";
import { buildTimeSlots, isManualTimeValid } from "../../../../utils/timeSlots";
import { Dayjs } from "dayjs";

const tagsColors = TAG_COLORS;

const Confirmation = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [start, setStart] = useState(dayjs());
    const [end, setEnd] = useState(dayjs());

    const [user, setUser] = useState<User | undefined>();
    const [image, setImage] = useState<string | null | undefined>();
    const [service, setService] = useState<Service | undefined>();

    const [confirmed, setConfirmed] = useState(false);
    const [note, setNote] = useState("");

    const [availableTime, setAvailableTime] = useState<string[]>([]);
    const [selectedTime, setSelectedTime] = useState<string | undefined>();

    // Custom time
    const [customTime, setCustomTime] = useState("");
    const [customTimeValid, setCustomTimeValid] = useState(false);

    const [loading, setLoading] = useState(false);

    // "Not in the past" clamp — only relevant when the free window is today.
    // Rounded up to the next full hour to match the previous preset behaviour.
    const notBefore = useMemo(() => {
        const nowOffset = dayjs().utcOffset(start.utcOffset());
        return start.isSame(nowOffset, "day")
            ? nowOffset.add(1, "hour").startOf("hour")
            : undefined;
    }, [start]);

    const buildCandidate = useCallback(
        (value: string): Dayjs | undefined => {
            if (!/^\d{2}:\d{2}$/.test(value)) return undefined;

            const [h, m] = value.split(":").map((n) => Number(n));
            if (h > 23 || m > 59) return undefined;

            return start
                .set("hour", h)
                .set("minute", m)
                .set("second", 0)
                .set("millisecond", 0);
        },
        [start]
    );

    const isCustomTimeValid = useCallback(
        (value: string) => {
            if (!service?.duration) return false;

            const candidate = buildCandidate(value);
            if (!candidate) return false;

            return isManualTimeValid(
                candidate,
                [{ start, end }],
                service.duration,
                notBefore
            );
        },
        [start, end, service, notBefore, buildCandidate]
    );

    useEffect(() => {
        const start = sessionStorage.getItem("slot_start");
        const end = sessionStorage.getItem("slot_end");
        if (start === null || end === null) {
            navigate("/calendar");
        } else {
            setStart(parseWithOffset(start));
            setEnd(parseWithOffset(end));
        }

        const user_id = sessionStorage.getItem("user_id");
        if (user_id === null) {
            navigate("/calendar/add/user");
        }

        const service_id = sessionStorage.getItem("service_id");
        if (service_id === null) {
            navigate("/calendar/add/service");
        }

        if (!uuid || !brand_id) return;

        User.info(uuid, brand_id, user_id!)
            .then((res) => {
                setUser(res);
                setImage(res?.image);
            })
            .catch(console.error);

        Service.info(uuid, brand_id, service_id!)
            .then(setService)
            .catch(console.error);
    }, [uuid, brand_id, navigate]);

    useEffect(() => {
        if (!service) return;

        setSelectedTime(undefined);

        const slots = buildTimeSlots(
            [{ start, end }],
            service.duration,
            notBefore
        );
        const times = slots.map((slot) => slot.format("HH:mm"));

        setAvailableTime(times);
        setSelectedTime(times[0]);
    }, [start, end, service, notBefore]);

    const onSave = useCallback(() => {
        const effectiveTime =
            selectedTime || (customTimeValid ? customTime : null);

        if (
            !uuid ||
            !brand_id ||
            !location_id ||
            !effectiveTime ||
            !service ||
            !user
        )
            return;
        setLoading(true);

        const time = effectiveTime.split(":").map((x) => Number(x));
        const dateTime = start.set("hours", time[0]).set("minutes", time[1]);

        // booking_id is only present if this appointment is already in the waitlist
        const bookingId = sessionStorage.getItem("booking_id");

        const cleanupSessionStorage = () => {
            [
                "slot_start",
                "slot_end",
                "user_id",
                "service_id",
                "booking_id",
                "booking_user_preselected",
                "booking_for_client_name",
            ].forEach((key) => sessionStorage.removeItem(key));
        };

        const wasClientFirst = !!sessionStorage.getItem(
            "booking_for_client_name"
        );

        const handleSuccess = () => {
            cleanupSessionStorage();
            if (sessionStorage.getItem("isNewUser")) {
                sessionStorage.removeItem("isNewUser");
                navigate(`/users/link/${user.key}`);
            } else if (wasClientFirst) {
                navigate(`/users/info/${user.key}`);
            } else navigate("/calendar");
        };

        const updateWaitlistBooking = (bookingId: string) => {
            const promises = [
                Booking.update(
                    uuid,
                    brand_id,
                    location_id,
                    bookingId,
                    dateTime.toDate(),
                    dateTime.add(service.duration, "minutes").toDate()
                ),
            ];

            if (confirmed) {
                promises.push(
                    Booking.updateConfirmed(
                        uuid,
                        brand_id,
                        location_id,
                        bookingId,
                        confirmed
                    )
                );
            }

            if (note.trim().length > 0) {
                promises.push(
                    Booking.updateNote(
                        uuid,
                        brand_id,
                        location_id,
                        bookingId,
                        note
                    )
                );
            }

            return Promise.all(promises);
        };

        if (bookingId) {
            updateWaitlistBooking(bookingId)
                .then(handleSuccess)
                .catch(console.error)
                .finally(() => setLoading(false));
        } else {
            Booking.register(
                uuid,
                brand_id,
                location_id,
                service.key,
                user.key,
                service.duration,
                service.price,
                dateTime.toDate(),
                dateTime.add(service.duration, "minutes").toDate(),
                confirmed,
                BookingState.Wait,
                note
            )
                .then(handleSuccess)
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [
        uuid,
        brand_id,
        location_id,
        confirmed,
        note,
        selectedTime,
        service,
        start,
        user,
        customTime,
        customTimeValid,
        navigate,
    ]);

    const statusColor = user?.status
        ? (tagsColors.find((x) => x.tag === user.status)?.color ?? primary)
        : primary;

    return (
        <>
            <TabHeader
                name="Appointment"
                onClick={() => navigate(-1 || "/calendar/add/service")}
            />
            <BookingForClientChip />
            <ContentAreaWrapper headerType="tab" noFooter stickyBottomButton>
                {/* ── User Identity Card ── */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                        ...CARD_STYLES,
                        width: "85%",
                        background: `linear-gradient(135deg, ${alpha(primary, 0.04)} 0%, ${alpha(primary, 0.08)} 100%)`,
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            minWidth: 44,
                            height: 44,
                            borderRadius: "14px",
                            backgroundImage: image
                                ? `url(${image})`
                                : undefined,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            bgcolor: !image
                                ? alpha(primary, 0.1)
                                : undefined,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                        }}
                    >
                        {!image && (
                            <PersonRounded
                                sx={{
                                    fontSize: 22,
                                    color: primary,
                                    opacity: 0.6,
                                }}
                            />
                        )}
                    </Box>
                    <Stack spacing={0.25} sx={{ overflow: "hidden" }}>
                        <Typography
                            fontFamily={FONTS.body}
                            fontWeight={600}
                            fontSize="15px"
                            lineHeight={1.3}
                            noWrap
                        >
                            {user?.name}
                        </Typography>
                        {user?.status && (
                            <Box
                                sx={{
                                    bgcolor: alpha(statusColor, 0.1),
                                    borderRadius: "10px",
                                    px: 0.75,
                                    py: 0.25,
                                    width: "fit-content",
                                }}
                            >
                                <Typography
                                    fontWeight={500}
                                    fontSize="11px"
                                    lineHeight={1.3}
                                    color={statusColor}
                                >
                                    {user.status}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Stack>

                {/* ── Service Card ── */}
                {service && (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            ...CARD_STYLES,
                            width: "85%",
                            p: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                minWidth: 64,
                                height: 64,
                                minHeight: 64,
                                borderRadius: "14px",
                                bgcolor: primary,
                                backgroundImage: `url(${service.image})`,
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "cover",
                                flex: "0 0 auto",
                            }}
                        />
                        <Stack
                            spacing={0.75}
                            justifyContent="center"
                            flex={1}
                            minWidth={0}
                        >
                            <Typography
                                fontFamily={FONTS.display}
                                fontWeight={600}
                                fontSize="16px"
                                lineHeight="20px"
                                minWidth={0}
                                sx={{
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                }}
                            >
                                {service.name}
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                            >
                                <FormattedPrice
                                    price={service.price}
                                    currency={service.currency}
                                />
                                <FormattedTime
                                    time={service.duration}
                                    size="small"
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                )}

                {/* ── Date Display ── */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    width="85%"
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={700}
                        fontSize="36px"
                        lineHeight={1}
                        color={primary}
                    >
                        {start.get("date")}
                    </Typography>
                    <Stack spacing={-0.25}>
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={600}
                            fontSize="16px"
                            color="text.primary"
                        >
                            {start.format("MMMM")}
                        </Typography>
                        <Typography
                            fontSize="13px"
                            color="text.secondary"
                        >
                            {start.format("dddd")}
                        </Typography>
                    </Stack>
                </Stack>

                {/* ── Time Selection ── */}
                {availableTime.length === 0 ? (
                    <Stack
                        alignItems="center"
                        spacing={1.5}
                        py={3}
                        width="85%"
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                bgcolor: alpha(primary, 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <AccessTimeRounded
                                sx={{
                                    fontSize: 24,
                                    color: primary,
                                    opacity: 0.5,
                                }}
                            />
                        </Box>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                        >
                            No available times
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        {/* Section label */}
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            width="85%"
                            textAlign="left"
                        >
                            Select time
                        </Typography>

                        {/* Time grid */}
                        <Stack sx={{ ...CARD_STYLES, width: "85%" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                {availableTime.map((time) => {
                                    const isSelected =
                                        time === selectedTime;
                                    return (
                                        <ButtonBase
                                            className="no-translation"
                                            key={time}
                                            sx={{
                                                width: 68,
                                                height: 42,
                                                borderRadius: "12px",
                                                bgcolor: isSelected
                                                    ? primary
                                                    : alpha(
                                                          primary,
                                                          0.06
                                                      ),
                                                transition:
                                                    "background-color 0.2s ease",
                                            }}
                                            onClick={() => {
                                                setSelectedTime(time);
                                                setCustomTime("");
                                            }}
                                        >
                                            <Typography
                                                fontFamily={FONTS.display}
                                                fontWeight={600}
                                                fontSize="15px"
                                                color={
                                                    isSelected
                                                        ? "white"
                                                        : "text.primary"
                                                }
                                            >
                                                {time}
                                            </Typography>
                                        </ButtonBase>
                                    );
                                })}
                            </Box>
                        </Stack>

                        {/* Custom time */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                ...CARD_STYLES,
                                width: "85%",
                                py: 1.5,
                            }}
                        >
                            <Typography sx={FORM_LABEL_SX}>
                                Custom start time
                            </Typography>
                            <TextField
                                type="time"
                                value={customTime}
                                onChange={(e) => {
                                    setCustomTime(e.target.value);
                                    setCustomTimeValid(true);
                                }}
                                onBlur={() => {
                                    setCustomTimeValid(
                                        isCustomTimeValid(customTime)
                                    );
                                    setSelectedTime(undefined);
                                }}
                                width={"120px"}
                                sx={{
                                    "& input::-webkit-calendar-picker-indicator":
                                        {
                                            display: "none",
                                            appearance: "none",
                                        },
                                }}
                                error={
                                    customTime.length > 0 &&
                                    !customTimeValid
                                }
                                helperText={
                                    customTime.length > 0 &&
                                    !customTimeValid
                                        ? "Unavailable"
                                        : " "
                                }
                            />
                        </Stack>
                    </>
                )}

                {/* ── Options ── */}
                <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    width="85%"
                    textAlign="left"
                >
                    Options
                </Typography>

                {/* Confirm toggle */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        ...CARD_STYLES,
                        width: "85%",
                        py: 1.5,
                    }}
                >
                    <Typography sx={FORM_LABEL_SX}>
                        Don't confirm
                    </Typography>
                    <ToggleButton
                        value={confirmed}
                        onClick={setConfirmed}
                    />
                </Stack>

                {/* Note */}
                <Stack sx={{ ...CARD_STYLES, width: "85%" }}>
                    <TextField
                        value={note}
                        onChange={(e) => setNote(e.currentTarget.value)}
                        placeholder="Note..."
                        fullWidth
                        multiline
                        variant="contained"
                        sx={{
                            bgcolor: "transparent",
                            fontWeight: 400,
                            fontSize: "15px",
                            lineHeight: "24.9px",
                        }}
                    />
                </Stack>

                {/* Save */}
                <BottomButton
                    onClick={onSave}
                    disabled={
                        loading ||
                        (!selectedTime && (!customTime || !customTimeValid))
                    }
                >
                    Save
                </BottomButton>
            </ContentAreaWrapper>
        </>
    );
};

export default Confirmation;
